import clsx from "clsx";
import React, { useEffect, useMemo } from "react";
import { Select } from "@pd/pd-ui-kit";
import { Control, Controller, useFieldArray, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useLicenseOwnerOptions } from "~/features/LicenseActionsModals/model/owners";
import {
  IAssignOrganizationValue,
  IAssignPositionValue,
  ILicenseAssignFormValues,
} from "~/features/LicenseActionsModals/model/types";
import { withoutTakenOptions } from "~/features/LicenseActionsModals/model/withoutTakenOptions";

import AssignPositionRow from "./AssignPositionRow";

import s from "~/features/LicenseActionsModals/LicenseActionsModals.less";

interface AssignOrganizationBlockProps {
  control: Control<ILicenseAssignFormValues>;
  organizationIndex: number;
  value: IAssignOrganizationValue;
  includeAllPartners?: boolean;
}

export const createEmptyPosition = (): IAssignPositionValue => ({
  position: null,
  employees: [{ employee: null }],
});

const AssignOrganizationBlock = ({
  control,
  organizationIndex,
  value,
  includeAllPartners = false,
}: AssignOrganizationBlockProps) => {
  const { t } = useTranslation();
  const fieldName = `organizations.${organizationIndex}`;

  const { options: organizationOptions, loading: organizationsLoading } =
    useLicenseOwnerOptions(includeAllPartners);

  const organizations =
    (useWatch({
      control,
      name: "organizations",
    }) as IAssignOrganizationValue[] | undefined) || [];

  const visibleOrganizationOptions = useMemo(
    () =>
      withoutTakenOptions(
        organizationOptions,
        organizations.map((item) => item.organization?.value),
        value.organization?.value,
      ),
    [organizationOptions, organizations, value.organization?.value],
  );

  const takenPositionValues = (value.positions ?? []).map(
    (item) => item.position?.value,
  );

  const {
    fields: positionFields,
    append: appendPosition,
    remove: removePositions,
  } = useFieldArray({
    control,
    name: `${fieldName}.positions`,
  });

  useEffect(() => {
    if (positionFields.length === 0) {
      appendPosition(createEmptyPosition());
    }
  }, [positionFields.length, appendPosition]);

  return (
    <div className={s.block}>
      <div className={s.field}>
        <span className={clsx(s.label, s.labelBold)}>
          {t("licenseActionsModals.organization")}
        </span>
        <Controller
          name={`${fieldName}.organization`}
          control={control}
          defaultValue={value.organization}
          render={({ onChange, value: selectValue }) => (
            <Select
              labelInValue
              showSearch
              optionFilterProp="label"
              value={selectValue}
              onChange={(option) => {
                onChange(option);
                removePositions();
                appendPosition(createEmptyPosition());
              }}
              options={visibleOrganizationOptions}
              loading={organizationsLoading}
              placeholder={t("licenseActionsModals.selectPlaceholder")}
            />
          )}
        />
      </div>
      {value.organization &&
        positionFields.map((positionField, positionIndex) => (
          <AssignPositionRow
            key={positionField.id}
            control={control}
            organizationIndex={organizationIndex}
            positionIndex={positionIndex}
            ownerValue={value.organization?.value || null}
            value={
              (value.positions ?? [])[positionIndex] || {
                position: null,
                employees: [],
              }
            }
            isLast={positionIndex === positionFields.length - 1}
            takenPositionValues={takenPositionValues}
            onAddPosition={() => appendPosition(createEmptyPosition())}
          />
        ))}
    </div>
  );
};

export default AssignOrganizationBlock;
